import { Link, useRouter, type Href } from 'expo-router';
import { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { EventRedirectHead } from '@/components/detail/event-head';
import { Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';

/**
 * What an old per-date URL (/[lang]/events/<row id>) renders: its static HTML
 * meta-refreshes to `path` — its show, or the city's events page once the show
 * is gone (EventRedirectHead), and once the app is
 * running — a client-side navigation to an old link — the router replaces it.
 * The visible link is only for the moment in between.
 */
export function EventRedirect({ id, path }: { id: string; path: string }) {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const href = `/${locale}/${path}` as Href;

  useEffect(() => {
    router.replace(href);
  }, [router, href]);

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      <EventRedirectHead id={id} path={path} locale={locale} />
      <Text style={[styles.text, { color: theme.mutedForeground }]}>{t('events.moved')}</Text>
      <Link
        href={href}
        style={[styles.text, { color: theme.gold }]}>
        {t('events.movedLink')}
      </Link>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  text: {
    fontSize: 15,
  },
});
